/**
 * Paywall Component
 * Displays subscription options with pricing
 */

import { useState, useEffect } from 'react';
import type { Package } from '@revenuecat/purchases-js';
import { useSubscription } from '@/hooks/useSubscription';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2, Check, Crown, Sparkles, MessageCircle, RefreshCw } from 'lucide-react';
import { cn } from '@/lib/utils';
import { trackPaywallViewed } from '@/lib/posthog';
import { usePlatformSettings } from '@/hooks/usePlatformSettings';

interface PaywallProps {
    onSuccess?: () => void;
    onClose?: () => void;
    initialPackageIdentifier?: string | null;
}

export function Paywall({ onSuccess, onClose, initialPackageIdentifier }: PaywallProps) {
    const {
        offerings,
        purchase,
        isLoading,
        error,
        restorePurchases,
        isSubscribed,
        referralBenefit,
    } = useSubscription();
    const { signOut } = useAuth();
    const { contactInfo } = usePlatformSettings();


    useEffect(() => {
        if (isSubscribed && onSuccess) {
            onSuccess();
        }
    }, [isSubscribed, onSuccess]);

    const handleLogout = async () => {
        await signOut();
    };

    const [selectedPackage, setSelectedPackage] = useState<Package | null>(null);
    const [isPurchasing, setIsPurchasing] = useState(false);

    useEffect(() => {
        trackPaywallViewed({ offerings_count: offerings.length });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!initialPackageIdentifier || selectedPackage) return;
        const requestedPackage = offerings.find((pkg) => {
            const pkgId = pkg.identifier.toLowerCase();
            const reqId = initialPackageIdentifier.toLowerCase();
            if (pkgId === reqId) return true;
            if ((reqId.includes('year') || reqId.includes('anual') || reqId.includes('annual')) &&
                (pkgId.includes('year') || pkgId.includes('anual') || pkgId.includes('annual'))) {
                return true;
            }
            if ((reqId.includes('month') || reqId.includes('mensual')) &&
                (pkgId.includes('month') || pkgId.includes('mensual'))) {
                return true;
            }
            return pkgId.includes(reqId);
        });
        if (requestedPackage) setSelectedPackage(requestedPackage);
    }, [initialPackageIdentifier, offerings, selectedPackage]);

    const handlePurchase = async () => {
        if (!selectedPackage) return;

        setIsPurchasing(true);
        const success = await purchase(selectedPackage);
        setIsPurchasing(false);

        if (success) {
            onSuccess?.();
        }
    };

    const getPackageDetails = (pkg: Package) => {
        const identifier = pkg.identifier.toLowerCase();
        const trialDays = referralBenefit?.trial_days || 0;
        const affiliateDescription = trialDays > 0
            ? `${trialDays} días gratis y ${referralBenefit?.discount_percent}% de descuento`
            : `${referralBenefit?.discount_percent}% de descuento aplicado`;

        if (identifier.includes('yearly') || identifier.includes('annual')) {
            return {
                name: 'Anual',
                description: referralBenefit ? affiliateDescription : 'Ahorra 40% con el plan anual',
                icon: Crown,
                popular: true,
                savings: referralBenefit
                    ? `Beneficio ${referralBenefit.code}: ${referralBenefit.discount_percent}% menos`
                    : 'Ahorro equivalente a 2 meses',
            };
        }

        return {
            name: 'Mensual',
            description: referralBenefit
                ? affiliateDescription
                : '3 días de prueba gratis, cancela cuando quieras',
            icon: Sparkles,
            popular: false,
            savings: referralBenefit
                ? `Beneficio ${referralBenefit.code}: ${referralBenefit.discount_percent}% menos`
                : '3 Días de Prueba Gratis',
        };
    };

    const getPackagePrice = (pkg: Package) => {
        const product = pkg.webBillingProduct || pkg.rcBillingProduct;
        const currentPrice = product?.currentPrice;
        const formattedPrice = currentPrice?.formattedPrice;
        const originalPrice = formattedPrice
            ? (formattedPrice.includes('MXN') ? formattedPrice : `${formattedPrice} MXN`)
            : 'Precio no disponible';

        if (!referralBenefit?.discount_percent || !currentPrice?.amountMicros) {
            return { originalPrice, discountedPrice: null };
        }

        const currency = currentPrice.currency || 'MXN';
        const discountedAmount = (currentPrice.amountMicros / 1_000_000)
            * (1 - referralBenefit.discount_percent / 100);
        const discountedPrice = new Intl.NumberFormat('es-MX', {
            style: 'currency',
            currency,
            minimumFractionDigits: 2,
        }).format(discountedAmount);

        return {
            originalPrice,
            discountedPrice: `${discountedPrice} ${currency}`,
        };
    };

    if (isLoading && offerings.length === 0) {
        return (
            <div className="flex items-center justify-center p-8">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    return (
        <div className="w-full max-w-4xl mx-auto p-1 sm:p-4">
            {/* Header */}
            <div className="text-center mb-4 md:mb-8">
                <h2 className="text-3xl font-bold mb-2">Desbloquea RepasoGPC Premium</h2>
                <p className="text-muted-foreground">
                    Accede a todo el contenido y prepárate para tu examen ENARM
                </p>
            </div>

            {referralBenefit && (
                <div className="mb-6 border border-emerald-500/30 bg-emerald-500/10 p-4 text-center">
                    <p className="font-semibold text-emerald-800 dark:text-emerald-300">
                        Beneficio de {referralBenefit.code}
                    </p>
                    <p className="text-sm text-emerald-700 dark:text-emerald-400">
                        {referralBenefit.trial_days
                            ? `${referralBenefit.trial_days} días gratis y después ${referralBenefit.discount_percent}% de descuento en cada cobro.`
                            : `${referralBenefit.discount_percent}% de descuento aplicado en cada cobro.`}
                    </p>
                </div>
            )}

            {/* Error Message */}
            {error && (
                <div className="bg-destructive/10 text-destructive p-4 rounded-lg mb-6 text-center">
                    {error}
                </div>
            )}


            {/* Note: Apple Pay requires domain verification in Stripe Dashboard -> Payment Methods -> Apple Pay */}

            {/* Plans */}
            <div className="grid md:grid-cols-2 gap-4 md:gap-6 mb-4 md:mb-8">
                {offerings.map((pkg) => {
                    const details = getPackageDetails(pkg);
                    const price = getPackagePrice(pkg);
                    const isSelected = selectedPackage?.identifier === pkg.identifier;
                    const Icon = details.icon;

                    return (
                        <Card
                            key={pkg.identifier}
                            className={cn(
                                'relative cursor-pointer transition-all hover:shadow-lg',
                                isSelected && 'ring-2 ring-primary shadow-lg',
                                details.popular && 'border-primary'
                            )}
                            onClick={() => setSelectedPackage(pkg)}
                        >
                            {details.popular && (
                                <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary">
                                    Más Popular
                                </Badge>
                            )}

                            <CardHeader className="p-4 md:p-6">
                                <div className="flex items-center gap-3">
                                    <div className={cn(
                                        'p-2 rounded-lg',
                                        details.popular ? 'bg-primary/10' : 'bg-muted'
                                    )}>
                                        <Icon className={cn(
                                            'h-6 w-6',
                                            details.popular ? 'text-primary' : 'text-muted-foreground'
                                        )} />
                                    </div>
                                    <div>
                                        <CardTitle>{details.name}</CardTitle>
                                        <CardDescription>{details.description}</CardDescription>
                                    </div>
                                </div>
                            </CardHeader>

                            <CardContent className="p-4 pt-0 md:p-6 md:pt-0">
                                {price.discountedPrice ? (
                                    <div>
                                        <div className="text-sm text-muted-foreground line-through">
                                            {price.originalPrice}
                                        </div>
                                        <div className="text-2xl md:text-3xl font-bold text-emerald-700 dark:text-emerald-400">
                                            {price.discountedPrice}
                                        </div>
                                    </div>
                                ) : (
                                    <div className="text-2xl md:text-3xl font-bold">
                                        {price.originalPrice}
                                    </div>
                                )}
                                {details.savings && (
                                    <Badge variant="secondary" className="mt-2">
                                        {details.savings}
                                    </Badge>
                                )}
                            </CardContent>

                            <CardFooter className="p-4 pt-0 md:p-6 md:pt-0">
                                <div className={cn(
                                    'w-6 h-6 rounded-full border-2 flex items-center justify-center',
                                    isSelected ? 'border-primary bg-primary' : 'border-muted-foreground'
                                )}>
                                    {isSelected && <Check className="h-4 w-4 text-primary-foreground" />}
                                </div>
                            </CardFooter>
                        </Card>
                    );
                })}
            </div>

            {/* Features */}
            <Card className="mb-4 md:mb-8">
                <CardHeader className="p-4 md:p-6">
                    <CardTitle className="text-lg">Incluido en Premium:</CardTitle>
                </CardHeader>
                <CardContent className="p-4 pt-0 md:p-6 md:pt-0">
                    <ul className="grid md:grid-cols-2 gap-3">
                        {[
                            'Acceso a todos los simulacros',
                            'Flashcards ilimitadas',
                            'Estadísticas avanzadas',
                            'Modo examen realista',
                            'Actualizaciones continuas',
                        ].map((feature) => (
                            <li key={feature} className="flex items-center gap-2">
                                <Check className="h-5 w-5 text-primary flex-shrink-0" />
                                <span>{feature}</span>
                            </li>
                        ))}
                    </ul>
                </CardContent>
            </Card>

            {/* Actions */}
            <div className="flex flex-col gap-3 md:gap-4">
                <Button
                    size="lg"
                    className="w-full"
                    disabled={!selectedPackage || isPurchasing || isLoading}
                    onClick={handlePurchase}
                >
                    {isPurchasing ? (
                        <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Procesando...
                        </>
                    ) : (
                        referralBenefit?.trial_days
                            ? `Comenzar ${referralBenefit.trial_days} Días Gratis`
                            : selectedPackage?.identifier.toLowerCase().includes('monthly') || selectedPackage?.identifier.toLowerCase().includes('mensual')
                                ? 'Comenzar Prueba Gratis de 3 Días'
                                : referralBenefit
                                    ? `Suscribirse con ${referralBenefit.discount_percent}% de Descuento`
                                    : 'Suscribirse Ahora'
                    )}
                </Button>

                <div className="flex flex-wrap justify-center gap-4 text-sm">
                    <Button
                        variant="link"
                        onClick={() => {
                            const whatsappNumber = contactInfo?.whatsapp || '525564888036';
                            const whatsappLink = `https://wa.me/${whatsappNumber}?text=Hola,%20necesito%20ayuda%20con%20mi%20suscripción%20en%20RepasoGPC`;
                            window.open(whatsappLink, '_blank', 'noopener,noreferrer');
                        }}
                        className="text-muted-foreground hover:text-foreground flex items-center gap-1.5"
                    >
                        <MessageCircle className="h-4 w-4 text-green-500" />
                        Contactar Soporte
                    </Button>
                    <Button
                        variant="link"
                        onClick={() => restorePurchases()}
                        disabled={isLoading || isPurchasing}
                        className="text-muted-foreground hover:text-foreground flex items-center gap-1.5"
                    >
                        <RefreshCw className="h-4 w-4 text-blue-500" />
                        Restaurar compras
                    </Button>
                    {onClose ? (
                        <Button variant="link" onClick={onClose}>
                            Continuar sin premium
                        </Button>
                    ) : (
                        <Button variant="link" onClick={handleLogout} className="text-muted-foreground hover:text-foreground">
                            Cerrar sesión y salir
                        </Button>
                    )}
                </div>
            </div>

            {/* Legal */}
            <p className="text-xs text-muted-foreground text-center mt-6">
                Al suscribirte, aceptas nuestros Términos de Servicio y Política de Privacidad.
                Gestiona tu suscripción cómodamente desde tu perfil en cualquier momento.
            </p>
        </div>
    );
}
